import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { generateClient } from 'aws-amplify/api';
import { 
  Sword, Shield, Heart, Scroll, Map, AlertCircle, 
  Save, RotateCcw, CheckCircle, Book, Compass, DicesIcon 
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Toast } from '../../components/ui/toast';
import { SaveStateManager, SaveStateEvents } from '../../utils/saveState/saveStateManager';
import { scenes } from './scenes';
import { getGameState } from '../../graphql/queries';
import { updateGameState } from '../../graphql/mutations';

const client = generateClient();

const WintermereGame = () => {
  const { gameId, characterId } = useParams();
  const navigate = useNavigate();
  
  // Game state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [gameState, setGameState] = useState(null);
  const [saving, setSaving] = useState(false);
  const [lastSaveTime, setLastSaveTime] = useState(null);
  const [showSaveToast, setShowSaveToast] = useState(false);
  const [gameLog, setGameLog] = useState([]);
  const [currentScene, setCurrentScene] = useState(null);
  
  // Refs
  const saveManagerRef = useRef(null);
  const unmountingRef = useRef(false);
  const logScrollRef = useRef(null);
  
  const AI_MODEL = process.env.REACT_APP_CLAUDE_MODEL;
  const MAX_TOKENS = parseInt(process.env.REACT_APP_MAX_TOKENS, 10);

    // Load checkpoint
    const handleLoadCheckpoint = async (checkpointIndex) => {
        if (!gameState || saving) return;

        try {
            setSaving(true);
            
            const loadedState = await saveManagerRef.current.loadCheckpoint(checkpointIndex);
            setGameState(loadedState);
            setCurrentScene(scenes[loadedState.metadata.sceneName]);
            
            addToLog('Checkpoint loaded');
            
        } catch (err) {
            console.error('Error loading checkpoint:', err);
            setError('Failed to load checkpoint');
        } finally {
            setSaving(false);
        }
        };

  // Initialize save manager and load game state
  useEffect(() => {
    const initGame = async () => {
      try {
        setLoading(true);
        
        // Create save manager instance
        saveManagerRef.current = new SaveStateManager(gameId, characterId);
        
        // Load initial state
        const savedState = await saveManagerRef.current.loadGameState();
        setGameState(savedState);
        setCurrentScene(scenes[savedState.metadata.sceneName]);
        
        // Load game log
        if (savedState.storyState.gameLog) {
          setGameLog(savedState.storyState.gameLog);
        }
        
        // Start auto-save
        saveManagerRef.current.startAutoSave(() => {
          handleSave(true);
        });
        
      } catch (err) {
        console.error('Error initializing game:', err);
        setError('Failed to load game state');
      } finally {
        setLoading(false);
      }
    };

    initGame();

    // Cleanup on unmount
    return () => {
      unmountingRef.current = true;
      if (saveManagerRef.current) {
        saveManagerRef.current.dispose();
      }
    };
  }, [gameId, characterId]);

  // Scroll to bottom of game log when it updates
  useEffect(() => {
    if (logScrollRef.current) {
      logScrollRef.current.scrollTop = logScrollRef.current.scrollHeight;
    }
  }, [gameLog]);

  const addToLog = (entry) => {
    const newEntry = {
      text: entry,
      timestamp: Date.now()
    };
    setGameLog(prev => [...prev, newEntry]);
    
    // Update game state with new log
    setGameState(prev => ({
      ...prev,
      storyState: {
        ...prev.storyState,
        gameLog: [...(prev.storyState.gameLog || []), newEntry]
      }
    }));
  };

  // Save game state
  const handleSave = useCallback(async (isAutoSave = false) => {
    if (!gameState || saving) return;

    try {
      setSaving(true);
      
      await saveManagerRef.current.saveGameState(gameState);
      
      setLastSaveTime(new Date());
      
      if (!isAutoSave) {
        setShowSaveToast(true);
        setTimeout(() => setShowSaveToast(false), 3000);
      }
    } catch (err) {
      console.error('Error saving game:', err);
      setError('Failed to save game state');
    } finally {
      setSaving(false);
    }
  }, [gameState, saving]);

  // Create checkpoint
  const handleCreateCheckpoint = async (checkpointName) => {
    if (!gameState || saving) return;

    try {
      setSaving(true);
      
      const checkpoint = await saveManagerRef.current.createCheckpoint(
        gameState,
        checkpointName
      );
      
      setGameState(prev => ({
        ...prev,
        characterState: {
          ...prev.characterState,
          checkpoints: [...prev.characterState.checkpoints, checkpoint]
        }
      }));
      
      addToLog(`Checkpoint created: ${checkpointName}`);
      
    } catch (err) {
      console.error('Error creating checkpoint:', err);
      setError('Failed to create checkpoint');
    } finally {
      setSaving(false);
    }
  };

  // Handle choice selection
  const handleChoice = async (choice) => {
    try {
      // Prepare context for AI DM
      const context = {
        character: gameState.characterState,
        currentScene,
        gameState,
        choice
      };

      // Call AI DM through API Gateway
      const response = await client.post('gameApi', '/dm/narrate', {
        body: context
      });

      // Update game state with AI response
      const { narration, consequences, newScene } = response.data;

      // Apply consequences
      if (consequences.experience) {
        setGameState(prev => ({
          ...prev,
          characterState: {
            ...prev.characterState,
            experience: prev.characterState.experience + consequences.experience
          }
        }));
      }

      // Add to game log
      addToLog(narration);

      // Update game state
      setGameState(prev => ({
        ...prev,
        metadata: {
          ...prev.metadata,
          sceneName: newScene.id,
          turnCount: prev.metadata.turnCount + 1
        },
        storyState: {
          ...prev.storyState,
          keyDecisions: [...prev.storyState.keyDecisions, {
            sceneId: currentScene.id,
            decision: choice.text,
            consequence: narration,
            timestamp: Date.now()
          }]
        }
      }));

      // Set new scene
      setCurrentScene(newScene);

      // Auto-save after significant choices
      await handleSave(true);

    } catch (err) {
      console.error('Error processing choice:', err);
      setError('Failed to process choice');
    }
  };

  if (loading) {
    return (
      <Card className="max-w-4xl mx-auto p-8">
        <CardContent className="flex justify-center items-center">
          <Scroll className="animate-spin mr-2 h-6 w-6" />
          <span>Loading game...</span>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>{error}</AlertDescription>
        <Button 
          className="mt-4" 
          variant="outline" 
          onClick={() => navigate('/games')}
        >
          Return to Games
        </Button>
      </Alert>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-4 space-y-4">
      {/* Game Header */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <span>{gameState.characterState.name}</span>
              <div className="flex items-center gap-2">
                <Heart className="text-red-500" />
                <span>
                  {gameState.characterState.currentHealth}/
                  {gameState.characterState.maxHealth}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <DicesIcon className="text-yellow-500" />
                <span>XP: {gameState.characterState.experience}</span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => handleSave()}
                disabled={saving}
              >
                <Save className="mr-2 h-4 w-4" />
                {saving ? 'Saving...' : 'Save Game'}
              </Button>
              
              {gameState.characterState.checkpoints.length > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleLoadCheckpoint(
                    gameState.characterState.checkpoints.length - 1
                  )}
                  disabled={saving}
                >
                  <RotateCcw className="mr-2 h-4 w-4" />
                  Load Last Checkpoint
                </Button>
              )}
            </div>
          </CardTitle>
        </CardHeader>
      </Card>

      {/* Main Game Content */}
      <Card>
        <CardHeader>
          <CardTitle>{currentScene?.title}</CardTitle>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-48 rounded-md border p-4">
            <p className="text-lg mb-4">{currentScene?.description}</p>
          </ScrollArea>
          
          <div className="grid gap-2 mt-4">
            {currentScene?.choices.map((choice, index) => (
              <Button
                key={index}
                onClick={() => handleChoice(choice)}
                className="w-full text-left justify-start"
                variant="outline"
                disabled={
                  choice.requires && 
                  !gameState.storyState.storyFlags.has(choice.requires)
                }
              >
                {choice.text}
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Game Log */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Book />
            Adventure Log
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-32" ref={logScrollRef}>
            {gameLog.map((entry, index) => (
              <p key={index} className="text-sm mb-2">
                {entry.text}
              </p>
            ))}
          </ScrollArea>
        </CardContent>
      </Card>

      {/* Save Toast */}
      {showSaveToast && (
        <Toast>
          <div className="flex items-center gap-2">
            <CheckCircle className="h-4 w-4 text-green-500" />
            <span>Game saved successfully</span>
          </div>
        </Toast>
      )}
    </div>
  );
};

export default WintermereGame;